
import * as React from 'react'

import { cn } from '@crm/shared/lib/styles/utils'
import { Field, FieldLabel, InputError, InputGroup, InputGroupInput } from '@crm/shared/ui'


import type { TFormInput } from '@crm/features/add-new-deal/model/form.schema'
import type {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPathByValue,
} from 'react-hook-form'

type DealTextFieldName = FieldPathByValue<TFormInput, string | number | undefined>

type DealTextFieldProps<TName extends DealTextFieldName> = Omit<
  React.ComponentProps<'input'>,
  'name' | 'value' | 'onChange' | 'onBlur' | 'ref'
> & {
  field: ControllerRenderProps<TFormInput, TName>
  fieldState: ControllerFieldState
  label: string
  labelClassName?: string
  formatter?: (value: string) => string
}

export const DealTextField = <TName extends DealTextFieldName>({
  field,
  fieldState,
  label,
  labelClassName,
  formatter,
  className,
  ...props
}: DealTextFieldProps<TName>) => {
  const inputValue =
    typeof field.value === 'string' || typeof field.value === 'number' ? field.value : ''

  return (
    <Field>
      <FieldLabel htmlFor={field.name} className={labelClassName}>
        {label}
      </FieldLabel>
      <InputGroup>
        <InputGroupInput
          id={field.name}
          aria-invalid={fieldState.invalid}
          aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
          className={cn(className)}
          {...field}
          {...props}
          value={inputValue}
          onChange={(event) =>
            field.onChange(
              formatter ? formatter(event.currentTarget.value) : event.currentTarget.value,
            )
          }
          onBlur={field.onBlur}
        />
        {fieldState.invalid && <InputError errorMessage={fieldState.error?.message} />}
      </InputGroup>
    </Field>
  )
}
